import React, { useRef, useState, useEffect, useCallback } from "react";
import { useTheme } from "@mui/material";
import { scaleLinear, scaleBand } from "@visx/scale";
import { AxisBottom, AxisLeft } from "@visx/axis";
import { useTooltip, TooltipWithBounds } from '@visx/tooltip';
import { localPoint } from '@visx/event';
import { getChartTooltipStyles } from "@utils/chart-styles";
import { colors } from "@utils/colors";

// Custom tick component for wrapping multi-word labels
const CustomTickLabel = ({ 
  x = 0, 
  y = 0, 
  text, 
  fill, 
  fontSize,
  textAnchor 
}: any) => {
  const maxDisplayedWords = 2; // Truncate labels to first 2 words if more than 2

  const formatted = String(text);
  let words = formatted.split(" ");
  // Truncate to first 2 words if more than 2
  if (words.length > maxDisplayedWords) {
    words = words.slice(0, maxDisplayedWords);
  }

  const lineHeight = 17;
  const startY = y + (words.length - 1) * (lineHeight / 2)

  return (
    <text x={x} y={startY} fill={fill} fontSize={fontSize} textAnchor={textAnchor}>
      {words.map((word: string, idx: number) => (
        <tspan key={`word-${idx}`} x={x} dy={idx === 0 ? 0 : lineHeight}>
          {word}
        </tspan>
      ))}
    </text>
  );
};

// Vertical tick label component for narrow widths with many labels
const VerticalTickLabel = ({ 
  x = 0, 
  y = 0, 
  text, 
  fill, 
  fontSize
}: any) => {
  const maxDisplayedWords = 2; // Truncate labels to first 2 words if more than 2
    const formatted = String(text);
    let words = formatted.split(" ");
    // Truncate to first 2 words if more than 2
    if (words.length > maxDisplayedWords) {
      words = words.slice(0, maxDisplayedWords);
    }
  const lineHeight = 14;
  
  return (
    <g transform={`rotate(-90, ${x}, ${y})`}>
      <text 
        x={x} 
        y={y} 
        fill={fill} 
        fontSize={fontSize} 
        textAnchor="end"
        dy="0.35em"
        dx="-0.5em"
      >
        {words.map((word: string, idx: number) => (
          <tspan key={`word-${idx}`} x={x} dx="-0.5em" dy={idx === 0 ? 0 : lineHeight}>
            {word}
          </tspan>
        ))}
      </text>
    </g>
  );
};

export interface HeatmapDatum {
  site: string;
  status: string;
  value: number;
}

export interface HeatmapChartProps {
  data: HeatmapDatum[];
  sites: string[];
  statuses: string[];
  colorRange?: [string, string];
  statusLabels?: Record<string, string>; // Optional: map status keys to display labels
}

const truncateLabel = (value: string, maxChars: number) => {
  if (value.length <= maxChars) return value;
  return `${value.slice(0, Math.max(0, maxChars - 1))}...`;
};

export const HeatmapChart: React.FC<HeatmapChartProps> = ({
  data,
  sites,
  statuses,
  colorRange = [colors.heatmapCold, colors.heatmapHot],
  statusLabels = {},
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState<{ width: number; height: number }>({ width: 0, height: 0 });
  const minHeight = 300;

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const updateDimensions = () => {
      const { width, height } = container.getBoundingClientRect();
      setDimensions({ 
        width, 
        height: Math.max(height, minHeight) 
      });
    };

    updateDimensions();

    const resizeObserver = new ResizeObserver(() => {
      updateDimensions();
    });

    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <div 
      ref={containerRef} 
      style={{ width: '100%', minHeight: `${minHeight}px`, height: '100%' }}
    >
      {dimensions.width > 0 && (
        <HeatmapChartInner
          data={data}
          sites={sites}
          statuses={statuses}
          width={dimensions.width}
          height={dimensions.height}
          colorRange={colorRange}
          statusLabels={statusLabels}
        />
      )}
    </div>
  );
};

interface TooltipData {
  site: string;
  status: string;
  value: number;
}

const HeatmapChartInner: React.FC<HeatmapChartProps & { width: number; height: number }> = ({
  data,
  sites,
  statuses,
  width = 700,
  height = 400,
  colorRange = [colors.heatmapCold, colors.heatmapHot],
  statusLabels = {},
}) => {
  const theme = useTheme();
  const tooltipStyles = getChartTooltipStyles(theme);
  const {
    showTooltip,
    hideTooltip,
    tooltipData,
    tooltipLeft,
    tooltipTop,
    tooltipOpen,
  } = useTooltip<TooltipData>();

  // Determine if we should use vertical labels
  const shouldUseVerticalLabels = sites.length > 6 && width < 900;
  const isNarrow = width < 700;
  const axisFontSize = isNarrow ? 11 : 13;
  const maxStatusChars = isNarrow ? 14 : 24;
  const maxSiteChars = isNarrow ? 12 : 20;

  // Build a matrix for fast lookup
  const valueMatrix: Record<string, Record<string, number>> = {};
  sites.forEach(site => {
    valueMatrix[site] = {};
    statuses.forEach(status => {
      valueMatrix[site][status] = 0;
    });
  });
  data.forEach(({ site, status, value }) => {
    if (!valueMatrix[site]) return;
    valueMatrix[site][status] = value;
  });

  // Find min/max for color scale
  const allValues = data.map(d => d.value);
  const minValue = Math.min(...allValues);
  const maxValue = Math.max(...allValues);

  // Calculate dynamic left margin based on longest status label
  const statusDisplayLabels = statuses.map((status) =>
    statusLabels[status as string] || status
  );
  const longestStatusLabel = Math.max(...statusDisplayLabels.map((s) => s.length));
  const estimatedLabelWidth = longestStatusLabel * (isNarrow ? 6.4 : 8);
  const maxLeftMargin = Math.floor(width * (isNarrow ? 0.34 : 0.45));
  const minLeftMargin = isNarrow ? 72 : 100;
  const calculatedLeftMargin = Math.min(
    Math.max(estimatedLabelWidth + 10, minLeftMargin),
    maxLeftMargin
  );
  
  // Margins - adjusted for wrapped labels at bottom (more space for vertical labels)
  const margin = {
    top: 20,
    left: calculatedLeftMargin,
    bottom: shouldUseVerticalLabels ? 110 : (isNarrow ? 84 : 100),
    right: isNarrow ? 12 : 20,
  };

  const innerWidth = Math.max(width - margin.left - margin.right, 120);
  const innerHeight = Math.max(height - margin.top - margin.bottom, 120);
  const xAxisTickCount = Math.min(sites.length, isNarrow ? 4 : 8);
  const legendX = Math.max(innerWidth - 80, 0);

  // Scales
  const xScale = scaleBand({
    domain: sites,
    range: [0, innerWidth],
    padding: 0.15,
  });
  const yScale = scaleBand({
    domain: statuses,
    range: [0, innerHeight],
    padding: 0.15,
  });
  const colorScale = scaleLinear({
    domain: [minValue, maxValue],
    range: colorRange,
  });

  // Handle cell hover
  const handleCellHover = useCallback(
    (event: React.MouseEvent<SVGRectElement>, site: string, status: string, value: number) => {
      const point = localPoint(event);
      if (!point) return;
      
      showTooltip({
        tooltipData: { site, status, value },
        tooltipLeft: point.x,
        tooltipTop: point.y,
      });
    },
    [showTooltip]
  );

  return (
    <div style={{ width: "100%", height: "100%", overflow: "hidden", position: "relative", color: theme.palette.text.secondary }}>
      <svg
        width="100%"
        height="100%"
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="xMidYMid meet"
        style={{ display: "block" }}
      >
        <defs>
          <filter id="heatmap-shadow" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="2" stdDeviation="3" floodOpacity="0.3" />
          </filter>
        </defs>
        <g transform={`translate(${margin.left},${margin.top})`}>
          {/* Cells */}
          {statuses.map((status) =>
            sites.map((site) => {
              const x = xScale(site);
              const y = yScale(status);
              const value = valueMatrix[site][status];
              const isHovered = tooltipOpen && tooltipData?.site === site && tooltipData?.status === status;
              return (
                <rect
                  key={`${site}-${status}`}
                  x={x}
                  y={y}
                  width={xScale.bandwidth()}
                  height={yScale.bandwidth()}
                  fill={colorScale(value)}
                  stroke="#fff"
                  rx={4}
                  style={{ cursor: 'pointer', filter: isHovered ? 'url(#heatmap-shadow)' : 'none' }}
                  onMouseMove={(e) => handleCellHover(e, site, status, value)}
                  onMouseLeave={hideTooltip}
                />
              );
            })
          )}

          {/* X-axis (Sites) */}
          <AxisBottom
            top={innerHeight}
            scale={xScale}
            numTicks={xAxisTickCount}
            tickComponent={({ formattedValue, ...tickProps }) => 
              shouldUseVerticalLabels ? (
                <VerticalTickLabel
                  {...tickProps}
                  text={truncateLabel(String(formattedValue), maxSiteChars)}
                  fill="currentColor"
                  fontSize={axisFontSize}
                />
              ) : (
                <CustomTickLabel
                  {...tickProps}
                  text={truncateLabel(String(formattedValue), maxSiteChars)}
                  fill="currentColor"
                  fontSize={axisFontSize}
                  textAnchor="middle"
                />
              )
            }
          />

          {/* Y-axis (Statuses) */}
          <AxisLeft
            scale={yScale}
            tickLabelProps={() => ({ fontSize: axisFontSize, textAnchor: "end", dx: "-0.5em", fill: "currentColor" })}
            tickFormat={(status) => {
              const display = statusLabels[status as string] || (status as string);
              return truncateLabel(display, maxStatusChars);
            }}
          />

          {/* Color legend */}
          <g transform={`translate(${legendX},${-15})`}>
            <defs>
              <linearGradient id="heatmap-gradient" x1="0" x2="1" y1="0" y2="0">
                <stop offset="0%" stopColor={colorRange[0]} />
                <stop offset="100%" stopColor={colorRange[1]} />
              </linearGradient>
            </defs>
            <rect x={0} y={-6} width={80} height={12} fill="url(#heatmap-gradient)" />
            <text x={0} y={18} fontSize={12} fill="currentColor">Low</text>
            <text x={80} y={18} fontSize={12} fill="currentColor" textAnchor="end">High</text>
          </g>
        </g>
      </svg>
      {/* Tooltip */}
      {tooltipOpen && tooltipData && (
        <TooltipWithBounds
          left={tooltipLeft}
          top={tooltipTop}
          style={tooltipStyles}
        >
          <div style={{ fontWeight: 600, marginBottom: '4px' }}>
            {tooltipData.site}
          </div>
          <div style={{ fontSize: '12px', color: theme.palette.text.secondary, marginBottom: '4px' }}>
            {statusLabels[tooltipData.status] || tooltipData.status}
          </div>
          <div style={{ fontSize: '14px', fontWeight: 500 }}>
            {tooltipData.value.toLocaleString()}
          </div>
        </TooltipWithBounds>
      )}
    </div>
  );
};
